/**
 * Word-Level LCS Highlighting with Color Superimposition
 * 
 * Matches at the word level and blends colors when multiple
 * models/sources match the same word.
 */

import { blendColors } from './colorBlending';

export interface WordMatch {
  word: string;
  startPos: number;
  endPos: number;
  colors: string[]; // All colors that match this word
  blendedColor: string; // Result of blending all colors
}

export interface WordSegment {
  text: string;
  isHighlighted: boolean;
  color?: string;
  matchCount?: number; // How many sources matched this word
}

/**
 * Tokenize text into words with their positions
 * Preserves whitespace and punctuation
 */
export function tokenizeWithPositions(text: string): Array<{
  word: string;
  startPos: number;
  endPos: number;
  isWord: boolean;
}> {
  const tokens: Array<{
    word: string;
    startPos: number;
    endPos: number;
    isWord: boolean;
  }> = [];
  
  // Match words (alphanumeric + hyphens) and non-words (spaces, punctuation)
  const regex = /[\w-]+|[^\w-]+/g;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    const token = match[0];
    const isWord = /[\w-]+/.test(token);
    
    tokens.push({
      word: token,
      startPos: match.index,
      endPos: match.index + token.length,
      isWord,
    });
  }
  
  return tokens;
}

/**
 * Build a word -> color map for a single text
 * Returns normalized words (lowercase) mapped to their color
 */
export function buildWordColorMap(
  text: string,
  color: string
): Map<string, string> {
  const wordMap = new Map<string, string>();
  const tokens = tokenizeWithPositions(text);
  
  for (const token of tokens) {
    if (token.isWord) {
      const normalizedWord = token.word.toLowerCase();
      wordMap.set(normalizedWord, color);
      
      // Also add apostrophe-stripped variant for matching
      // This ensures "let's" matches "lets", "you're" matches "youre", etc.
      const strippedWord = normalizedWord.replace(/'/g, '');
      if (strippedWord !== normalizedWord && strippedWord.length > 0) {
        wordMap.set(strippedWord, color);
      }
    }
  }
  
  return wordMap;
}

/**
 * Find word-level matches with color blending
 * 
 * @param targetText - Text to highlight (e.g., synthesis)
 * @param sourceTextsWithColors - Array of {text, color} to match against
 * @returns Segments with blended colors where multiple sources match
 */
export function findWordMatches(
  targetText: string,
  sourceTextsWithColors: Array<{ text: string; color: string }>
): WordSegment[] {
  // Build word maps for each source
  const sourceMaps = sourceTextsWithColors.map((source) =>
    buildWordColorMap(source.text, source.color)
  );
  
  // Tokenize target text
  const tokens = tokenizeWithPositions(targetText);
  
  // Build segments with color blending
  const segments: WordSegment[] = [];
  
  for (const token of tokens) {
    if (!token.isWord) {
      // Non-word tokens (spaces, punctuation) are never highlighted
      segments.push({
        text: token.word,
        isHighlighted: false,
      });
      continue;
    }
    
    // Check which sources have this word
    const normalizedWord = token.word.toLowerCase();
    const matchingColors: string[] = [];
    
    for (const sourceMap of sourceMaps) {
      // Try exact match first
      if (sourceMap.has(normalizedWord)) {
        matchingColors.push(sourceMap.get(normalizedWord)!);
      } else {
        // Try apostrophe-stripped variant
        const strippedWord = normalizedWord.replace(/'/g, '');
        if (strippedWord !== normalizedWord && sourceMap.has(strippedWord)) {
          matchingColors.push(sourceMap.get(strippedWord)!);
        }
      }
    }
    
    if (matchingColors.length > 0) {
      // Word matches one or more sources - blend the colors
      segments.push({
        text: token.word,
        isHighlighted: true,
        color: blendColors(matchingColors),
        matchCount: matchingColors.length,
      });
    } else {
      // No match
      segments.push({
        text: token.word,
        isHighlighted: false,
      });
    }
  }
  
  return segments;
}

/**
 * Create word-level highlighting layers for synthesis
 * Each active model contributes its color to matching words
 */
export function createSynthesisWordHighlighting(
  synthesisText: string,
  candidateTextsWithColors: Array<{ text: string; color: string }>
): WordSegment[] {
  return findWordMatches(synthesisText, candidateTextsWithColors);
}

/**
 * Create word-level highlighting for a candidate
 * Shows which words appear in the synthesis (ionoff color)
 */
export function createCandidateWordHighlighting(
  candidateText: string,
  synthesisText: string,
  ionoffColor: string
): WordSegment[] {
  return findWordMatches(candidateText, [
    { text: synthesisText, color: ionoffColor },
  ]);
}

